import dayjs from 'dayjs';
import { type StatusBadgeTone } from '@/app/modules/table/components/StatusBadge';
import {
  type Ticket,
  type TicketCategory,
  type TicketDetailsValues,
  TicketDueFilter,
  type TicketDepartment,
  type TicketFieldValue,
  type TicketFormField,
  TicketListTab,
  TicketSla,
  TicketStatus,
  type TicketSubjectPath,
} from './_models';

const STATUS_BADGES: Record<TicketStatus, { label: string; tone: StatusBadgeTone }> = {
  [TicketStatus.NEW]: { label: 'Novo', tone: 'primary' },
  [TicketStatus.IN_PROGRESS]: { label: 'Em atendimento', tone: 'info' },
  [TicketStatus.WAITING_STORE]: { label: 'Aguardando você', tone: 'warning' },
  [TicketStatus.RESOLVED]: { label: 'Resolvido', tone: 'success' },
};

const TAB_LABELS: Record<TicketListTab, string> = {
  [TicketListTab.OPEN]: 'Em aberto',
  [TicketListTab.WAITING]: 'Aguardando você',
  [TicketListTab.RESOLVED]: 'Resolvidos',
  [TicketListTab.ALL]: 'Todos',
};

const SLA_HOURS: Record<TicketSla, number> = {
  [TicketSla.HOURS_8]: 8,
  [TicketSla.HOURS_24]: 24,
  [TicketSla.HOURS_48]: 48,
};

const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

export function ticketStatusBadge(status: TicketStatus) {
  return STATUS_BADGES[status];
}

export function ticketsInTab(tickets: Ticket[], tab: TicketListTab): Ticket[] {
  if (tab === TicketListTab.ALL) return tickets;
  if (tab === TicketListTab.OPEN) return tickets.filter((ticket) => ticket.status !== TicketStatus.RESOLVED);
  if (tab === TicketListTab.WAITING) return tickets.filter((ticket) => ticket.status === TicketStatus.WAITING_STORE);
  return tickets.filter((ticket) => ticket.status === TicketStatus.RESOLVED);
}

const DUE_FILTER_LABELS: Record<TicketDueFilter, string> = {
  [TicketDueFilter.LATE]: 'Atrasados',
  [TicketDueFilter.RESOLVED_ON_TIME]: 'No prazo',
  [TicketDueFilter.RESOLVED_LATE]: 'Fora do prazo',
};

export const TICKET_DUE_FILTER_OPTIONS = Object.values(TicketDueFilter).map((due) => ({
  value: due,
  label: DUE_FILTER_LABELS[due],
}));

function ticketMatchesDue(late: boolean, resolvedOnTime: boolean | undefined, due: TicketDueFilter): boolean {
  if (due === TicketDueFilter.LATE) return late;
  if (due === TicketDueFilter.RESOLVED_ON_TIME) return resolvedOnTime === true;
  return resolvedOnTime === false;
}

export function ticketsMatchingFilter(
  tickets: Ticket[],
  department: string | undefined,
  store: string | undefined,
  due: TicketDueFilter | undefined,
): Ticket[] {
  return tickets.filter((ticket) => {
    if (department && ticket.path.department !== department) return false;
    if (store && ticket.store !== store) return false;
    if (due && !ticketMatchesDue(ticket.late, ticket.resolvedOnTime, due)) return false;
    return true;
  });
}

export function activeTicketFilterCount(
  department: string | undefined,
  store: string | undefined,
  due: TicketDueFilter | undefined,
): number {
  return [department, store, due].filter(Boolean).length;
}

export function ticketTabLabel(tab: TicketListTab): string {
  return TAB_LABELS[tab];
}

export function slaLabel(sla: TicketSla): string {
  return `${SLA_HOURS[sla]} h`;
}

export function slaHourCount(sla: TicketSla): number {
  return SLA_HOURS[sla];
}

export function slaBusinessHoursLabel(sla: TicketSla): string {
  return `${SLA_HOURS[sla]} horas úteis`;
}

type SubjectLookup = TicketSubjectPath & { sla: TicketSla; form: TicketFormField[]; instruction?: string };

export function findSubjectPath(catalog: TicketDepartment[], subjectId: string): SubjectLookup | undefined {
  for (const department of catalog) {
    for (const category of department.categories) {
      const subject = category.subjects.find((item) => item.id === subjectId);
      if (subject) {
        return {
          subjectId,
          department: department.name,
          category: category.name,
          subject: subject.name,
          sla: subject.sla,
          form: subject.form,
          instruction: subject.instruction,
        };
      }
    }
  }
  return undefined;
}

export function ticketTitle(subjectName: string, form: TicketFormField[], fields: TicketFieldValue[]): string {
  const titleLabels = form.filter((field) => field.showInTitle).map((field) => field.label);
  const titleValues = titleLabels
    .map((label) => fields.find((field) => field.label === label)?.value ?? '')
    .filter((value) => value.trim() !== '');
  return [subjectName, ...titleValues].join(' · ');
}

export function dueWeekdayLabel(dueAt: string): string {
  const due = dayjs(dueAt);
  const weekdayName = WEEKDAY_NAMES[due.day()];
  const dateLabel = due.format('DD/MM, HH:mm');
  return `${weekdayName}, ${dateLabel}`;
}

const COMBINING_MARK = /[\u0300-\u036f]/u;

export function normalizeSearchText(text: string): string {
  const decomposed = text.normalize('NFD');
  const baseCharacters = [...decomposed].filter((character) => !COMBINING_MARK.test(character));
  return baseCharacters.join('').toLowerCase().trim();
}

export function filterCatalogBySearch(catalog: TicketDepartment[], search: string): TicketDepartment[] {
  const searchText = normalizeSearchText(search);
  if (searchText === '') return catalog;

  return catalog
    .map((department) => ({
      ...department,
      categories: department.categories
        .map((category) => ({
          ...category,
          subjects: category.subjects.filter((subject) => {
            const haystack = normalizeSearchText(`${subject.name} ${subject.hint} ${category.name}`);
            return haystack.includes(searchText);
          }),
        }))
        .filter((category) => category.subjects.length > 0),
    }))
    .filter((department) => department.categories.length > 0);
}

export function departmentSubjectCount(categories: TicketCategory[]): number {
  return categories.reduce((total, category) => total + category.subjects.length, 0);
}

export function emptyTicketFields(form: TicketFormField[]): TicketFieldValue[] {
  return form.map((field) => ({ label: field.label, value: '' }));
}

export function initialDetailsValues(
  form: TicketFormField[],
  prefillFields: TicketFieldValue[] = [],
  prefillDescription = '',
): TicketDetailsValues {
  const fields = form.map((field) => {
    const prefilled = prefillFields.find((item) => item.label === field.label);
    return { label: field.label, value: prefilled?.value ?? '' };
  });
  return { store: '', fields, description: prefillDescription, attachments: [] };
}

export function detailsValuesForSubject(values: TicketDetailsValues, form: TicketFormField[]): TicketDetailsValues {
  return { ...values, fields: emptyTicketFields(form) };
}

export function ticketFieldLabelClass(required: boolean): string {
  const baseClass = 'form-label fw-bold fs-7 text-gray-700';
  return required ? `${baseClass} required` : baseClass;
}
